import { type ClassValue, clsx } from 'clsx';

// Simple cn utility using clsx for class name merging
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export function formatCurrency(amount: number, currency: string = 'NOK'): string {
  return new Intl.NumberFormat('nb-NO', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

export function resolveApiBaseUrl(configuredUrl?: string): string {
  const fallback = 'http://localhost:5011/api';
  const raw = configuredUrl?.trim() || fallback;

  if (typeof window === 'undefined') {
    return raw;
  }

  try {
    const parsed = new URL(raw);
    const currentHost = window.location.hostname;
    const configIsLocal = LOCAL_HOSTNAMES.has(parsed.hostname);
    const currentIsLocal = LOCAL_HOSTNAMES.has(currentHost);

    // Helpful in LAN/mobile dev: if config uses localhost but page is opened from device IP.
    if (configIsLocal && !currentIsLocal) {
      parsed.hostname = currentHost;
      return parsed.toString().replace(/\/$/, '');
    }

    return parsed.toString().replace(/\/$/, '');
  } catch {
    return fallback;
  }
}

const DEFAULT_BOOKING_URL = '/contact?intent=schedule-consultation';

export function resolveBookingUrl(configuredUrl?: string): string {
  const raw = configuredUrl?.trim();
  if (!raw) {
    return DEFAULT_BOOKING_URL;
  }

  // Accept full URLs and mailto/tel links.
  if (/^(https?:\/\/|mailto:|tel:)/i.test(raw)) {
    return raw;
  }

  // Preserve site-relative routes used for in-app scheduling flows.
  if (/^(\/|\.\/|\.\.\/|\?|#)/.test(raw)) {
    return raw;
  }

  // If a host is provided without protocol, assume https.
  return `https://${raw}`;
}

export function hasDirectBookingCalendar(configuredUrl?: string): boolean {
  const raw = configuredUrl?.trim();
  if (!raw) {
    return false;
  }

  if (/^(mailto:|tel:)/i.test(raw)) {
    return false;
  }

  return !/^(\/|\.\/|\.\.\/|\?|#)/.test(raw);
}
